import { useLayoutEffect, useRef, useState } from 'react'

const PLANS_HEADER_ART_SELECTOR = '[data-plans-header-art]'

type HeaderHeroDescriptionProps = {
  description: string
  maxLines?: number
  className?: string
  onLearnMoreClick?: () => void
}

export function HeaderHeroDescription({
  description,
  maxLines,
  className,
  onLearnMoreClick
}: HeaderHeroDescriptionProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const descriptionTextRef = useRef<HTMLParagraphElement>(null)
  const [showMoreLink, setShowMoreLink] = useState(false)
  const artClearanceWidth = useHeaderArtClearanceWidth(descriptionTextRef, description)

  useLayoutEffect(() => {
    const descriptionElement = descriptionTextRef.current
    if (!descriptionElement || isExpanded || maxLines == null || onLearnMoreClick != null) {
      setShowMoreLink(false)
      return
    }
    setShowMoreLink(descriptionElement.scrollHeight > descriptionElement.clientHeight + 1)
  }, [description, isExpanded, maxLines, onLearnMoreClick])

  if (description.length === 0) {
    return null
  }

  const clampStyle =
    maxLines != null && !isExpanded && onLearnMoreClick == null
      ? {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical' as const,
        WebkitLineClamp: maxLines,
        overflow: 'hidden'
      }
      : undefined

  const wrapperClassName = className?.length
    ? `pt-4 text-[13px] leading-[150%] text-[#6A6E6E] ${className}`
    : 'pt-4 text-[13px] leading-[150%] text-[#6A6E6E]'

  return (
    <div
      className={wrapperClassName}
      style={artClearanceWidth != null ? { maxWidth: artClearanceWidth } : undefined}
    >
      <p ref={descriptionTextRef} style={clampStyle}>
        {onLearnMoreClick != null ? description.replace(/\.+$/, '') : description}
        {onLearnMoreClick != null ? (
          <>
            {'. '}
            <span
              className="underline cursor-pointer"
              onClick={(event) => {
                event.stopPropagation()
                onLearnMoreClick()
              }}
            >
              Learn more
            </span>
          </>
        ) : null}
      </p>
      {showMoreLink && !isExpanded ? (
        <div
          className="font-[400] text-[#6A6E6E] underline cursor-pointer"
          onClick={() => setIsExpanded(true)}
        >
          more
        </div>
      ) : null}
    </div>
  )
}

/** Narrows the description to the art's left edge while the paragraph shares a vertical band with the art. */
function useHeaderArtClearanceWidth(
  descriptionRef: { current: HTMLElement | null },
  description: string
) {
  const [artClearanceWidth, setArtClearanceWidth] = useState<number | null>(null)

  useLayoutEffect(() => {
    const descriptionElement = descriptionRef.current
    const headerArt = descriptionElement ? findPlansHeaderArt(descriptionElement) : null
    if (!descriptionElement || !headerArt) {
      return
    }

    const measureArtClearance = () => {
      // Paragraph only. The wrapper's top padding sits above the words and is not overlap.
      const descriptionRect = descriptionElement.getBoundingClientRect()
      const artRect = headerArt.getBoundingClientRect()
      const sitsBesideArt = descriptionRect.top < artRect.bottom && descriptionRect.bottom > artRect.top
      const availableWidth = Math.floor(artRect.left - descriptionRect.left)
      const nextWidth = sitsBesideArt && availableWidth > 0 ? availableWidth : null
      setArtClearanceWidth((currentWidth) => (
        isSameArtClearance(currentWidth, nextWidth) ? currentWidth : nextWidth
      ))
    }

    measureArtClearance()
    const resizeObserver = new ResizeObserver(measureArtClearance)
    resizeObserver.observe(descriptionElement)
    resizeObserver.observe(headerArt)
    window.addEventListener('resize', measureArtClearance)
    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', measureArtClearance)
    }
  }, [description, descriptionRef])

  return artClearanceWidth
}

function isSameArtClearance(currentWidth: number | null, nextWidth: number | null) {
  if (currentWidth == null || nextWidth == null) {
    return currentWidth === nextWidth
  }
  return Math.abs(currentWidth - nextWidth) < 1
}

function findPlansHeaderArt(descriptionElement: HTMLElement): Element | null {
  let ancestor = descriptionElement.parentElement
  while (ancestor) {
    const headerArt = ancestor.querySelector(PLANS_HEADER_ART_SELECTOR)
    if (headerArt) {
      return headerArt
    }
    ancestor = ancestor.parentElement
  }
  return null
}
